// TanStack Query hooks for the RFC-0006 doc read endpoints (RFC-0010 Task 3.1;
// ADR-0007). Wraps `apiGet` (lib/api-client.ts) and inherits the shared retry
// predicate from the queryClient (no 401/4xx retries).
//
// `GET /api/projects/:repo/docs?path=` is dual-mode: with `?path=` it returns a
// single-doc detail (`DocDetail`), without it a paginated list. The reader uses
// detail mode (`useProjectDoc`); the list hook is provided for completeness and
// for callers that enumerate a repo's docs.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { DocDetail } from '@portal/shared-types';
import { apiGet } from '@/lib/api-client';
import { projectKeys, type Paginated } from '@/lib/queries/projects';

/** A single row in the doc-list response (RFC-0006 §4 list-item shape). */
export interface DocListItem {
  path: string;
  title: string | null;
  docType: string | null;
  docStatus: string | null;
  docOwner: string | null;
  version: number;
  bytes: number | null;
  updatedAt: string;
  /** Aggregate per-doc view count (RFC-0017). Additive; `0` when unviewed. */
  viewCount: number;
  /** ISO timestamp of the most recent view (RFC-0017). Additive; `null` when unviewed. */
  lastViewedAt: string | null;
}

/**
 * `GET /api/projects/:repo/docs?path=<path>[&version=]` — detail mode. Returns
 * the raw Markdown body plus frontmatter fields for one doc. Disabled until both
 * `repo` and `path` are set (so the project page can defer the fetch until a doc
 * is selected).
 */
export function useProjectDoc(
  repo: string | undefined,
  path: string | undefined,
  version?: number,
): UseQueryResult<DocDetail, Error> {
  return useQuery({
    queryKey: projectKeys.doc(repo ?? '', path ?? '', version),
    queryFn: () =>
      apiGet<DocDetail>(`/api/projects/${encodeURIComponent(repo!)}/docs`, {
        path: path!,
        version,
      }),
    enabled: Boolean(repo) && Boolean(path),
  });
}

/**
 * `GET /api/projects/:repo/docs` — list mode (no `?path=`): the current doc set
 * (one row per path at its latest version), paginated. Disabled until `repo` is
 * set.
 */
export function useProjectDocs(
  repo: string | undefined,
): UseQueryResult<Paginated<DocListItem>, Error> {
  return useQuery({
    queryKey: projectKeys.docs(repo ?? ''),
    queryFn: () =>
      apiGet<Paginated<DocListItem>>(`/api/projects/${encodeURIComponent(repo!)}/docs`),
    enabled: Boolean(repo),
  });
}
